/**
 * PlanChip — Dashboard topbar pill showing the user's selected plan.
 *
 * On mount, if the URL contains `?subscribed=1&persona=...&modules=...`
 * (set by either the pricing-first signup flow OR the post-signup
 * checkout redirect), we POST the carried selection to
 * `/api/billing/subscribe` so the tenant doc gets `selected_plan`
 * persisted server-side.  Then we fetch `/api/billing/my-plan` to read
 * back the canonical record and display it as a compact violet pill.
 *
 * The chip is purely informational — no trial, no countdown — clicking
 * it routes to /pricing for upgrades or to /dashboard/settings/billing
 * for invoice management.
 *
 * Empty / unauthed → renders nothing.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import api from '@/lib/api';

export default function PlanChip() {
  const [plan, setPlan] = useState(null);
  const location = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(location.search);
    const subscribed = params.get('subscribed') === '1';
    const persona = params.get('persona');
    const billing = params.get('billing');
    const modulesRaw = params.get('modules');

    const persist = async () => {
      // If we landed here with a carried plan, POST it to the backend
      // so the tenant doc is updated, then strip the params from the URL
      // (without a page reload) so a refresh doesn't re-fire the call.
      if (subscribed && persona && modulesRaw) {
        try {
          await api.post('/billing/subscribe', {
            persona,
            billing: billing || 'monthly',
            modules: modulesRaw.split(',').filter(Boolean),
          });
        } catch {
          /* silent — non-blocking */
        }
        // Clean URL
        try {
          const url = new URL(window.location.href);
          ['subscribed', 'persona', 'billing', 'modules'].forEach((k) => url.searchParams.delete(k));
          window.history.replaceState({}, '', url.toString());
        } catch { /* ignore */ }
      }
      try {
        const { data } = await api.get('/billing/my-plan');
        if (!cancelled) setPlan(data?.selected_plan || null);
      } catch {
        /* silent — visitor may have no plan yet */
      }
    };
    persist();
    return () => { cancelled = true; };
  }, [location.search]);

  if (!plan) return null;

  return (
    <button
      type="button"
      onClick={() => nav('/pricing')}
      data-testid="plan-chip"
      className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)] hover:bg-[rgba(174,82,234,0.12)] hover:border-[rgba(174,82,234,0.55)] transition-colors group"
      title="Click to review or change your plan"
    >
      <Sparkles size={11} className="text-[var(--ve-violet-bright)]" />
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--ve-violet-bright)] capitalize">
        {plan.persona}
      </span>
      <span className="text-[10px] opacity-50">·</span>
      <span className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-80">
        {plan.module_count}M · {plan.billing_cycle === 'annual' ? 'YR' : 'MO'}
      </span>
    </button>
  );
}
